import React, { useEffect, useRef, useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import CanonicalCardImage from "@/components/CanonicalCardImage";
import MembershipCardModal from "@/components/MembershipCardModal";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, BadgeCheck, KeyRound, FileText, Plus, X, Copy, Gift, Eye } from "lucide-react";
import { inr } from "@/lib/format";
import { ageLabel } from "@/lib/age";
import { allowedRelationsFor } from "@/lib/relations";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function CustomerDashboard() {
  const { user } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [newMember, setNewMember] = useState({ full_name: "", mobile: "", dob: "", relation: "", aadhaar: "" });
  const [addBusy, setAddBusy] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  // When user clicks per-row View on a sub-member, we open the modal pre-
  // selected to that member + side so the preview is exactly what they
  // intended (no extra click on the row chip inside the modal).
  const [viewerInitial, setViewerInitial] = useState({ memberId: null, side: "front" });

  useEffect(() => {
    api.get("/memberships/my").then((r) => {
      setMemberships(r.data);
      if (r.data.length) setSelected(r.data[0]);
    });
    api.get("/invoices/my").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);

  // Fetch the per-member card roster whenever the active membership changes.
  // Backend returns a `cards[]` array — one row per member (primary +
  // sub-members), each with `member_id`, `full_name`, `relation`, etc.
  useEffect(() => {
    if (!selected?.membership_id) { setCards([]); return; }
    let cancelled = false;
    setCardsLoading(true);
    api.get(`/memberships/by-id/${selected.membership_id}/cards`)
      .then((r) => { if (!cancelled) setCards(r.data?.cards || []); })
      .catch((e) => { if (!cancelled) { console.error("cards fetch failed", e); setCards([]); } })
      .finally(() => { if (!cancelled) setCardsLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.membership_id]);

  const submitAddMember = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!/^\d{10}$/.test(newMember.mobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    if (!newMember.full_name.trim() || !newMember.dob || !newMember.relation) {
      toast.error("Please complete all fields"); return;
    }
    if (newMember.aadhaar && newMember.aadhaar.length !== 12) {
      toast.error("Aadhaar must be 12 digits"); return;
    }
    setAddBusy(true);
    try {
      const r = await api.post(`/memberships/${selected.membership_id}/members`, newMember);
      toast.success(`${newMember.full_name} added to your plan`);
      const refreshed = r.data.membership;
      if (refreshed) {
        setMemberships((ms) => ms.map((x) => x.membership_id === selected.membership_id ? refreshed : x));
        setSelected(refreshed);
      }
      setAddMemberOpen(false);
      setNewMember({ full_name: "", mobile: "", dob: "", relation: "", aadhaar: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add member");
    } finally { setAddBusy(false); }
  };

  const downloadInvoice = async (inv) => {
    try {
      const safe = inv.invoice_no.replace(/[^A-Za-z0-9._-]/g, "-");
      await downloadFile(`/invoices/by-no/${encodeURIComponent(inv.invoice_no)}/pdf`, `NPC-Invoice-${safe}.pdf`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download invoice");
    }
  };

  const downloadCard = async () => {
    if (!selected) return;
    try {
      await downloadFile(`/memberships/by-id/${selected.membership_id}/card.png`, `NPC-Card-${selected.member_id || selected.membership_id}.png`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download card");
    }
  };

  const downloadMemberCard = async (card) => {
    if (!selected) return;
    try {
      const params = card.is_primary ? "" : `?member_id=${encodeURIComponent(card.member_id)}`;
      const safe = (card.full_name || card.member_id || "member").replace(/[^A-Za-z0-9._-]/g, "-");
      await downloadFile(
        `/memberships/by-id/${selected.membership_id}/card-both.pdf${params}`,
        `NPC-Card-${card.member_id}-${safe}.pdf`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not download card");
    }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated");
      setPwOpen(false); setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setPwBusy(false); }
  };

  return (
    <SiteLayout>
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="customer-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="overline">Member dashboard</span>
            <h1 className="mt-2 font-display text-4xl font-bold text-[#0F2540]">Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""} 👋</h1>
            <p className="mt-1 text-[#5C6B7A]">Your memberships, payments, and digital card — all in one place.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setPwOpen(true)} className="btn-ghost" data-testid="change-password-btn"><KeyRound className="h-4 w-4" /> Change password</button>
            <Link to="/plans" className="btn-primary">Add another plan</Link>
          </div>
        </div>

        {pwOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => !pwBusy && setPwOpen(false)}>
            <form onSubmit={submitPw} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-3" data-testid="change-password-form">
              <h3 className="font-display text-xl font-bold text-[#0F2540]">Change password</h3>
              <p className="text-xs text-[#5C6B7A]">Set a strong new password (min 8 characters).</p>
              <input type="password" required placeholder="Current password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} className="w-full rounded-xl border border-[#E1E8F0] px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="cp-current" />
              <input type="password" required placeholder="New password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} className="w-full rounded-xl border border-[#E1E8F0] px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="cp-new" />
              <input type="password" required placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full rounded-xl border border-[#E1E8F0] px-4 py-2.5 outline-none focus:border-[#1E4F87]" data-testid="cp-confirm" />
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" className="btn-ghost" onClick={() => setPwOpen(false)} disabled={pwBusy}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={pwBusy} data-testid="cp-submit">{pwBusy ? "Saving…" : "Update password"}</button>
              </div>
            </form>
          </div>
        )}

        {memberships.length === 0 ? (
          <div className="mt-12 rounded-3xl bg-white border border-[#E1E8F0] p-10 text-center">
            <BadgeCheck className="h-10 w-10 text-[#1E4F87] mx-auto" />
            <h3 className="mt-4 font-display text-2xl font-bold text-[#0F2540]">No active memberships yet</h3>
            <p className="mt-1 text-[#5C6B7A]">Pick a plan to activate your Nirmal Hospital membership.</p>
            <Link to="/plans" className="btn-primary mt-6 inline-flex">Browse plans</Link>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <CanonicalCardImage
                membershipId={selected?.membership_id}
                testId="membership-card"
              />
              <div className="mt-6 flex flex-wrap gap-2">
                <button
                  onClick={() => { setViewerInitial({ memberId: null, side: "front" }); setViewerOpen(true); }}
                  disabled={!selected}
                  className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="dashboard-view-card-btn"
                >
                  <Eye className="h-4 w-4" /> View card (front + back)
                </button>
                <button onClick={downloadCard} className="btn-ghost inline-flex items-center gap-2" data-testid="dashboard-download-btn">
                  <Download className="h-4 w-4" /> Download PNG
                </button>
              </div>

              {/* All membership cards (primary + sub-members) — front+back PDF
                  download per row. Shown only when the plan carries >1 member. */}
              {cards.length > 1 && (
                <div className="mt-6 rounded-2xl border border-[#E1E8F0] bg-white overflow-hidden" data-testid="dashboard-family-cards">
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E1E8F0] bg-[#F8FAFD]">
                    <h4 className="text-sm font-semibold text-[#0F2540]">All membership cards <span className="text-[11px] text-[#5C6B7A] font-normal">· {cards.length} card{cards.length > 1 ? "s" : ""}</span></h4>
                  </div>
                  <div className="divide-y divide-[#E1E8F0]">
                    {cardsLoading && (
                      <div className="px-4 py-5 text-sm text-[#5C6B7A] text-center">Loading…</div>
                    )}
                    {!cardsLoading && cards.map((c) => (
                      <div key={c.member_id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`dashboard-family-row-${c.member_id}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-[#0F2540] truncate">{c.full_name || "—"}</span>
                            {c.is_primary && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#1E4F87] text-white font-semibold uppercase tracking-wider">Primary</span>
                            )}
                            {c.relation && !c.is_primary && (
                              <span className="text-[11px] text-[#5C6B7A] capitalize">· {c.relation}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-[#5C6B7A] font-mono mt-0.5">{c.member_id}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            onClick={() => {
                              setViewerInitial({ memberId: c.is_primary ? null : c.member_id, side: "front" });
                              setViewerOpen(true);
                            }}
                            className="text-xs px-3 py-1.5 rounded-full border border-[#1E4F87] text-[#1E4F87] hover:bg-[#1E4F87] hover:text-white transition-colors inline-flex items-center gap-1.5"
                            data-testid={`dashboard-family-view-${c.member_id}`}
                          >
                            <Eye className="h-3.5 w-3.5" /> View
                          </button>
                          <button
                            onClick={() => downloadMemberCard(c)}
                            className="text-xs px-3 py-1.5 rounded-full border border-[#E1E8F0] text-[#0F2540] hover:bg-[#F4F7FB] transition-colors inline-flex items-center gap-1.5"
                            data-testid={`dashboard-family-pdf-${c.member_id}`}
                          >
                            <FileText className="h-3.5 w-3.5" /> PDF
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected?.discount_code && (
                <DiscountCodeCard code={selected.discount_code} />
              )}
              {selected && (
                <FamilyOnPlanWidget membership={selected}
                  onAddClick={() => setAddMemberOpen(true)}
                  onMembershipUpdate={(next) => {
                    setMemberships((ms) => ms.map((x) => x.membership_id === selected.membership_id ? next : x));
                    setSelected(next);
                  }} />
              )}
            </div>
            <div className="lg:col-span-7 space-y-4">
              {memberships.map((m) => (
                <button key={m.membership_id} onClick={() => setSelected(m)}
                  className={`w-full text-left rounded-2xl p-6 border transition-all ${selected?.membership_id === m.membership_id ? "border-[#1E4F87] bg-white shadow-sm" : "border-[#E1E8F0] bg-white/70"}`}
                  data-testid={`membership-${m.member_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-xl text-[#0F2540]">{m.plan_name} Plan</div>
                      <div className="text-sm text-[#5C6B7A] font-mono">{m.member_id}</div>
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-[#1F7A3D]/10 text-[#1F7A3D] font-semibold uppercase">{m.status}</span>
                  </div>
                  <div className="mt-3 text-xs text-[#5C6B7A]">
                    Valid till {new Date(m.valid_till).toLocaleDateString("en-IN")} · {m.family_members_allowed} members
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {addMemberOpen && selected && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="add-member-modal">
            <div className="bg-white rounded-3xl max-w-lg w-full p-7 shadow-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display text-2xl font-bold text-[#0F2540]">Add a family member</h3>
                  <p className="text-sm text-[#5C6B7A] mt-1">
                    Parents, spouse, or children can be added to your plan. They&#39;ll get their own member ID and access to NPC benefits.
                  </p>
                </div>
                <button type="button" onClick={() => setAddMemberOpen(false)} className="text-[#5C6B7A] hover:text-[#0F2540]" aria-label="Close">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={submitAddMember} className="mt-5 space-y-4">
                <div>
                  <label className="text-sm font-medium text-[#0F2540]">Full Name *</label>
                  <input required value={newMember.full_name}
                    onChange={(e) => setNewMember({ ...newMember, full_name: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                    data-testid="add-member-name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-[#0F2540]">Relation *</label>
                    <select required value={newMember.relation}
                      onChange={(e) => setNewMember({ ...newMember, relation: e.target.value })}
                      className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                      data-testid="add-member-relation">
                      <option value="">Select</option>
                      {allowedRelationsFor(selected?.plan_tier, (selected?.members || []).filter((m) => !m.is_primary)).map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#0F2540]">Mobile *</label>
                    <input required value={newMember.mobile}
                      onChange={(e) => setNewMember({ ...newMember, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                      className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                      data-testid="add-member-mobile" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F2540]">Date of Birth *</label>
                  <input type="date" required value={newMember.dob}
                    onChange={(e) => setNewMember({ ...newMember, dob: e.target.value })}
                    className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87]"
                    data-testid="add-member-dob" />
                  {newMember.dob && (
                    <p className="text-[11px] text-[#5C6B7A] mt-1" data-testid="add-member-age">Age · {ageLabel(newMember.dob)}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm font-medium text-[#0F2540]">Aadhaar Number *</label>
                  <input
                    inputMode="numeric"
                    placeholder="12-digit Aadhaar"
                    value={newMember.aadhaar}
                    onChange={(e) => setNewMember({ ...newMember, aadhaar: e.target.value.replace(/\D/g, "").slice(0, 12) })}
                    className="mt-1 w-full rounded-xl border border-[#E1E8F0] bg-white px-4 py-2.5 outline-none focus:border-[#1E4F87] font-mono"
                    data-testid="add-member-aadhaar" />
                </div>
                <p className="text-xs text-[#5C6B7A]">
                  KYC documents (Aadhaar &amp; passport-size photo) can be uploaded from this dashboard after adding the member. Admin will verify and mark KYC complete.
                </p>
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setAddMemberOpen(false)} disabled={addBusy} className="btn-ghost">Cancel</button>
                  <button type="submit" disabled={addBusy} className="btn-primary" data-testid="add-member-submit">
                    {addBusy ? "Adding…" : "Add member"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* My Visits + Benefit Wallet — only when there's an active membership */}
        {selected && (
          <MemberVisitsAndBenefits memberId={selected.member_id} />
        )}

        <div className="mt-12" data-testid="my-invoices-section">
          <h2 className="font-display text-2xl font-bold text-[#0F2540]">Invoices</h2>
          <p className="text-sm text-[#5C6B7A]">Download a printable GST invoice for any of your purchases.</p>
          <div className="mt-4 rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
            <table className="w-full text-sm" data-testid="my-invoices-table">
              <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
                <tr>
                  <th className="text-left px-4 py-3">Invoice</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Plan</th>
                  <th className="text-left px-4 py-3">Amount</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_no} className="border-t border-[#E1E8F0]">
                    <td className="px-4 py-3 font-mono text-xs">{inv.invoice_no}</td>
                    <td className="px-4 py-3 text-[#5C6B7A]">{new Date(inv.issued_at).toLocaleDateString("en-IN")}</td>
                    <td className="px-4 py-3">{inv.plan_name}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F2540]">{inr(inv.final ?? inv.gross)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => downloadInvoice(inv)} className="text-sm text-[#1E4F87] underline inline-flex items-center gap-1" data-testid={`dl-invoice-${inv.invoice_no.replace(/\W/g, "_")}`}>
                        <FileText className="h-3.5 w-3.5" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#5C6B7A]">No invoices yet — they appear here after your first purchase.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {viewerOpen && selected && (
        <MembershipCardModal
          membership={selected}
          mode="customer"
          initialMemberId={viewerInitial.memberId}
          initialSide={viewerInitial.side}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </SiteLayout>
  );
}

function FamilyOnPlanWidget({ membership, onAddClick, onMembershipUpdate }) {
  // Legacy fallback — older memberships may have empty `members[]`.
  // Synthesize a primary member from top-level fields so the customer can
  // still see / upload docs for themselves.
  const rawMembers = membership?.members || [];
  const members = rawMembers.length > 0 ? rawMembers : [{
    member_id: membership?.member_id || "primary",
    is_primary: true,
    full_name: membership?.full_name || "Primary member",
    mobile: membership?.mobile || "",
    dob: membership?.dob || "",
    relation: "self",
    aadhaar: membership?.aadhaar || "",
    aadhaar_doc_id: membership?.aadhaar_doc_id,
    photo_doc_id: membership?.photo_doc_id,
    kyc_status: membership?.kyc_status || "pending",
    kyc_remarks: membership?.kyc_remarks || "",
  }];
  const allowed = membership?.family_members_allowed || 1;
  const slotsLeft = Math.max(0, allowed - members.length);
  const verifiedCount = members.filter((m) => (m.kyc_status || "pending") === "verified").length;
  const pct = members.length ? Math.round((verifiedCount / members.length) * 100) : 0;
  return (
    <div className="mt-6 rounded-2xl bg-white border border-[#E1E8F0] p-5 shadow-sm" data-testid="family-on-plan-widget">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-4">
          <KycRing pct={pct} verified={verifiedCount} total={members.length} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6B7A] font-bold">My family on this plan</div>
            <div className="mt-0.5 text-sm text-[#0F2540]">
              <b>{members.length}</b> of <b>{allowed}</b> covered
              {slotsLeft > 0 && <span className="text-[#5C6B7A]"> · {slotsLeft} slot{slotsLeft === 1 ? "" : "s"} open</span>}
            </div>
            <div className="mt-0.5 text-[11px] text-[#5C6B7A]" data-testid="kyc-progress-caption">
              {verifiedCount} of {members.length} KYC verified
            </div>
          </div>
        </div>
        {slotsLeft > 0 && allowed > 1 && (
          <button onClick={onAddClick}
            className="btn-primary text-xs inline-flex items-center gap-1.5 px-3 py-1.5"
            data-testid="widget-add-member-btn">
            <Plus className="h-3.5 w-3.5" /> Add member
          </button>
        )}
      </div>
      {members.length > 0 && (
        <div className="mt-4 space-y-3">
          {members.map((sm) => (
            <MemberDocCard key={sm.member_id} membership={membership} sm={sm}
              onChange={onMembershipUpdate} />
          ))}
        </div>
      )}
    </div>
  );
}

function KycRing({ pct, verified, total }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  const complete = verified === total && total > 0;
  return (
    <div className="relative flex-shrink-0" data-testid="kyc-progress-ring">
      <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
        <circle cx="28" cy="28" r={r} fill="none" stroke="#E1E8F0" strokeWidth="5" />
        <circle cx="28" cy="28" r={r} fill="none"
          stroke={complete ? "#1F7A3D" : "#1E4F87"}
          strokeWidth="5" strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-[11px] font-bold text-[#0F2540]">
        {pct}%
      </div>
    </div>
  );
}

const RELATION_LABEL = { self: "Primary", spouse: "Spouse", father: "Father", mother: "Mother",
                         son: "Son", daughter: "Daughter" };

const KYC_TONE = {
  verified: "bg-[#1F7A3D]/10 text-[#1F7A3D] border-[#1F7A3D]/20",
  rejected: "bg-[#A53A2A]/10 text-[#A53A2A] border-[#A53A2A]/20",
  pending:  "bg-[#C89B3C]/15 text-[#7A5A1F] border-[#C89B3C]/30",
};

function MemberDocCard({ membership, sm, onChange }) {
  const aadhaarRef = useRef(null);
  const photoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({}); // {aadhaar_doc_id: 0..100}

  const rel = (sm.relation || "").toLowerCase();
  const tag = sm.is_primary ? "Primary" : (RELATION_LABEL[rel] || (sm.relation || "Member"));
  const kycStatus = (sm.kyc_status || "pending").toLowerCase();
  const kycLabel = kycStatus === "verified" ? "KYC Verified" : kycStatus === "rejected" ? "KYC Rejected" : "KYC Pending";

  const patchDocs = async (patch) => {
    const r = await api.put(
      `/memberships/my/${membership.membership_id}/members/${sm.member_id}/docs`,
      patch,
    );
    onChange(r.data);
  };

  const upload = async (file, key) => {
    if (!file) return;
    setBusy(true);
    setProgress((p) => ({ ...p, [key]: 10 }));
    try {
      const data_base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onprogress = (e) => { if (e.total) setProgress((p) => ({ ...p, [key]: Math.round((e.loaded / e.total) * 60) })); };
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      setProgress((p) => ({ ...p, [key]: 70 }));
      const folder = key === "photo_doc_id" ? "members" : "aadhaar";
      const up = await api.post("/uploads/document", { filename: file.name, content_type: file.type, data_base64, folder });
      setProgress((p) => ({ ...p, [key]: 90 }));
      await patchDocs({ [key]: up.data.doc_id });
      setProgress((p) => ({ ...p, [key]: 100 }));
      toast.success("Uploaded · KYC reset to Pending for re-verification");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      setTimeout(() => setProgress((p) => { const { [key]: _, ...rest } = p; return rest; }), 800);
    }
  };

  const view = async (docId) => {
    if (!docId) return;
    try {
      const r = await api.get(`/uploads/document-public/${docId}`);
      const src = r.data.url
        ? r.data.url
        : (r.data.data_base64 ? `data:${r.data.content_type};base64,${r.data.data_base64}` : null);
      if (!src) { toast.error("Document unavailable"); return; }
      const isPdf = r.data.content_type === "application/pdf";
      const w = window.open("", "_blank", "width=900,height=900");
      if (!w) { toast.error("Pop-up blocked"); return; }
      // Safe DOM rendering — avoids document.write() XSS via filename
      try {
        w.document.title = String(r.data.filename || "Document");
        const body = w.document.body;
        if (isPdf) {
          const iframe = w.document.createElement("iframe");
          iframe.src = src;
          iframe.style.cssText = "border:0;width:100%;height:100vh";
          body.appendChild(iframe);
        } else {
          body.style.cssText = "margin:0;background:#0F2540;display:grid;place-items:center";
          const img = w.document.createElement("img");
          img.src = src;
          img.style.cssText = "max-width:96vw;max-height:96vh";
          body.appendChild(img);
        }
      } catch (renderErr) {
        console.error("Document viewer render failed", renderErr);
        try { w.close(); } catch (e) { /* ignore */ }
        toast.error("Could not open");
      }
    } catch (e) { console.error("view document failed", e); toast.error("Could not open"); }
  };

  const DocAction = ({ label, docId, onPick, testidSuffix, fieldKey }) => {
    const prog = progress[fieldKey];
    return (
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className="text-[#5C6B7A] w-14 font-medium">{label}</span>
        {prog !== undefined ? (
          <div className="flex-1 min-w-[120px] max-w-[200px]">
            <div className="h-1.5 rounded-full bg-[#E1E8F0] overflow-hidden">
              <div className="h-full bg-[#1E4F87] transition-all" style={{ width: `${prog}%` }} />
            </div>
            <span className="text-[10px] text-[#5C6B7A] mt-0.5 inline-block">Uploading… {prog}%</span>
          </div>
        ) : docId ? (
          <>
            <span className="inline-flex items-center gap-1 text-[#1F7A3D] font-semibold">
              <BadgeCheck className="h-3.5 w-3.5" /> Uploaded
            </span>
            <button onClick={() => view(docId)} className="text-[#1E4F87] underline">View</button>
            <button onClick={onPick} className="text-[#A53A2A] underline" disabled={busy}>Replace</button>
          </>
        ) : (
          <button onClick={onPick} className="text-[#A53A2A] underline font-semibold" disabled={busy}
            data-testid={`upload-${testidSuffix}-${sm.member_id}`}>
            + Upload
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-[#E1E8F0] bg-[#F8FAFD] p-4 hover:border-[#1E4F87]/30 transition-colors"
      data-testid={`member-row-${sm.member_id}`}>
      {/* Header row — name + badges */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-[#0F2540]">{sm.full_name || "—"}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
              sm.is_primary ? "bg-[#1E4F87]/10 text-[#1E4F87]" : "bg-[#F4F7FB] text-[#5C6B7A]"
            }`}>{tag}</span>
            <span
              title={sm.kyc_remarks || ""}
              className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold inline-flex items-center gap-1 border ${KYC_TONE[kycStatus]}`}
              data-testid={`kyc-badge-${sm.member_id}`}>
              {kycStatus === "verified" && <BadgeCheck className="h-3 w-3" />}
              {kycLabel}
            </span>
          </div>
          <div className="mt-1 text-[11px] font-mono text-[#5C6B7A]">{sm.member_id}{sm.uhid ? ` · UHID ${sm.uhid}` : ""}</div>
          <div className="text-[11px] text-[#5C6B7A]">
            {sm.mobile || "—"} · DOB {sm.dob || "—"}{sm.dob ? ` (${ageLabel(sm.dob)})` : ""}
            {sm.aadhaar && <> · Aadhaar <span className="font-mono">XXXX-XXXX-{sm.aadhaar.slice(-4)}</span></>}
          </div>
          {kycStatus === "rejected" && sm.kyc_remarks && (
            <div className="mt-2 text-[11px] text-[#A53A2A] bg-[#A53A2A]/5 border border-[#A53A2A]/20 rounded-md px-2 py-1"
              data-testid={`kyc-remarks-${sm.member_id}`}>
              <b>Admin note:</b> {sm.kyc_remarks}
            </div>
          )}
        </div>
      </div>

      {/* Document actions */}
      <div className="mt-3 grid sm:grid-cols-2 gap-2 sm:gap-4">
        <DocAction label="Aadhaar" docId={sm.aadhaar_doc_id} testidSuffix="aadhaar" fieldKey="aadhaar_doc_id"
          onPick={() => aadhaarRef.current?.click()} />
        <DocAction label="Photo" docId={sm.photo_doc_id} testidSuffix="photo" fieldKey="photo_doc_id"
          onPick={() => photoRef.current?.click()} />
      </div>

      <input ref={aadhaarRef} type="file" className="hidden" accept="image/*,application/pdf"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, "aadhaar_doc_id"); e.target.value = ""; }} />
      <input ref={photoRef} type="file" className="hidden" accept="image/*"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, "photo_doc_id"); e.target.value = ""; }} />
    </div>
  );
}

function DiscountCodeCard({ code }) {
  // Share-friendly URL — friends land on /join with the referral_code pre-filled.
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/join?ref=${encodeURIComponent(code)}`
    : `/join?ref=${encodeURIComponent(code)}`;

  const writeClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta);
  };

  const copyCode = async () => {
    try { await writeClipboard(code); toast.success("Discount code copied"); }
    catch (e) { console.error(e); toast.error("Could not copy — long-press to select"); }
  };
  const copyLink = async () => {
    try { await writeClipboard(shareUrl); toast.success("Share link copied — paste it anywhere"); }
    catch (e) { console.error(e); toast.error("Could not copy link"); }
  };
  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join NPC with my code",
          text: `Use my Nirmal Privilege Card code ${code} to save on your membership.`,
          url: shareUrl,
        });
        return;
      } catch { /* user cancelled */ }
    }
    copyLink();
  };

  return (
    <div className="mt-6 rounded-2xl border-2 border-[#C89B3C]/40 bg-gradient-to-br from-[#FFF7E6] to-white p-5" data-testid="discount-code-card">
      <div className="flex items-start gap-3">
        <Gift className="h-5 w-5 text-[#A0741F] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-[#A0741F] font-bold">Your discount code</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="font-mono text-lg font-bold text-[#0F2540] tracking-wide px-3 py-1.5 rounded-lg bg-white border border-[#C89B3C]/30" data-testid="discount-code-value">{code}</code>
            <button onClick={copyCode} className="btn-ghost !text-xs !px-3 !py-2" data-testid="discount-code-copy">
              <Copy className="h-3.5 w-3.5" /> Copy code
            </button>
            <button onClick={shareNative} className="btn-ghost !text-xs !px-3 !py-2" data-testid="discount-code-share">
              <Copy className="h-3.5 w-3.5" /> Share link
            </button>
          </div>
          <p className="text-xs text-[#5C6B7A] mt-2">Share this with friends &amp; family — when they join with your code, <strong className="text-[#0F2540]">your membership is automatically extended</strong>. The share link auto-fills your code for them.</p>
        </div>
      </div>
    </div>
  );
}



function MemberVisitsAndBenefits({ memberId }) {
  const [stats, setStats] = useState(null);
  const [visits, setVisits] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engagementOn, setEngagementOn] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/maintenance-status")
      .then((r) => { if (alive) setEngagementOn(!!r.data.engagement_module_enabled); })
      .catch(() => { if (alive) setEngagementOn(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!memberId || !engagementOn) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/members/${memberId}/visit-stats`).catch(() => null),
      api.get(`/members/${memberId}/visits?limit=10`).catch(() => null),
      api.get(`/members/${memberId}/benefits`).catch(() => null),
    ]).then(([s, v, w]) => {
      if (!alive) return;
      setStats(s?.data || null);
      setVisits(v?.data?.items || []);
      setWallet(w?.data || null);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [memberId, engagementOn]);

  if (loading || !engagementOn) return null;
  const hasAny = (stats && stats.total_visits > 0) || (wallet && (wallet.benefits || []).length > 0);
  if (!hasAny) return null;

  return (
    <div className="mt-12 space-y-8" data-testid="my-engagement-section">
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SmallStat label="Total Visits" value={stats?.total_visits ?? 0} />
        <SmallStat label="Total Savings" value={inr(stats?.total_savings ?? 0)} tone="gold" />
        <SmallStat label="Avg Monthly" value={stats?.avg_monthly_visits ?? 0} />
        <SmallStat label="Last Visit" value={stats?.last_visit_date ? new Date(stats.last_visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"} />
      </div>

      {/* Benefit wallet */}
      {wallet && (wallet.benefits || []).length > 0 && (
        <div data-testid="my-benefit-wallet">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-[#0F2540]">My Benefit Wallet</h2>
            <span className="text-sm font-semibold text-[#1F7A3D]">Total saved · {inr(wallet.total_savings || 0)}</span>
          </div>
          <p className="text-sm text-[#5C6B7A] mb-3">Plan benefits attached to your {wallet.plan_name}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {wallet.benefits.map((b) => {
              const used = b.used_count || 0;
              const total = b.total_allowed;
              const pct = total ? Math.min(100, Math.round((used / total) * 100)) : 0;
              return (
                <div key={b.benefit_id} className="rounded-2xl bg-white border border-[#E1E8F0] p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-[#0F2540]">{b.title}</div>
                      <div className="text-xs text-[#5C6B7A] mt-0.5">{b.category} · {b.reset_period}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#5C6B7A]">Saved</div>
                      <div className="text-sm font-bold text-[#1F7A3D]">{inr(b.savings_generated || 0)}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-[#5C6B7A]">
                      <span>{total == null ? "Unlimited" : `${used} of ${total} used`}</span>
                      {total != null && <span>{b.remaining_count} left</span>}
                    </div>
                    <div className="mt-1 h-2 bg-[#F2F2F2] rounded-full overflow-hidden">
                      <div className="h-full bg-[#1E4F87]" style={{ width: `${total == null ? 0 : pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Visits timeline */}
      {visits.length > 0 && (
        <div data-testid="my-visits-section">
          <h2 className="font-display text-2xl font-bold text-[#0F2540]">My Visits</h2>
          <p className="text-sm text-[#5C6B7A] mb-3">Your recent hospital visits and savings</p>
          <div className="rounded-2xl bg-white border border-[#E1E8F0] overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EAF1FA] text-[#5C6B7A]">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Department</th>
                  <th className="text-left px-4 py-3">Benefits Used</th>
                  <th className="text-right px-4 py-3">Bill</th>
                  <th className="text-right px-4 py-3">Saved</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((v) => (
                  <tr key={v.visit_id} className="border-t border-[#E1E8F0]" data-testid={`my-visit-${v.visit_id}`}>
                    <td className="px-4 py-3 text-[#0F2540]">{new Date(v.visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-4 py-3 text-[#0F2540]">{v.department}<div className="text-xs text-[#5C6B7A]">{v.visit_type}{v.doctor_name ? ` · ${v.doctor_name}` : ""}</div></td>
                    <td className="px-4 py-3">
                      {(v.benefits_applied || []).length === 0 ? (
                        <span className="text-xs text-[#5C6B7A]">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 max-w-[260px]">
                          {(v.benefits_applied || []).map((b, idx) => (
                            <span key={idx} title={`Saved ₹${b.savings || 0}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#FAF1DC] text-[#8C6A00] border border-[#F0D58A] rounded-full px-2 py-0.5">
                              {b.title}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-[#0F2540]">{inr(v.bill_amount || 0)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#1F7A3D]">{inr(v.total_savings || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SmallStat({ label, value, tone = "navy" }) {
  return (
    <div className={`rounded-2xl border p-4 ${tone === "gold" ? "border-[#F0D58A] bg-[#FAF1DC]" : "border-[#E1E8F0] bg-white"}`}>
      <div className="text-xs uppercase tracking-wider text-[#5C6B7A]">{label}</div>
      <div className="font-display text-xl font-bold text-[#0F2540] mt-1">{value}</div>
    </div>
  );
}
